import { useState } from 'react';
import { ExternalLink, MoreVertical } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu.tsx';
import { tourRoute } from '@/features/dashboard/tours/routes/tourRoute.ts';
import timeUtils from '@/utils/timeUtils.ts';
import TourStatusIndicator from '@/features/dashboard/tours/components/TourStatusIndicator.tsx';
import useDeleteTour from '@/features/dashboard/tourConfig/hooks/useDeleteTour.ts';
import useActivateTour from '@/features/dashboard/tourConfig/hooks/useActivateTour.ts';
import useOpenRegistration from '@/features/dashboard/tourConfig/hooks/useOpenRegistration.ts';
import useCloseRegistration from '@/features/dashboard/tourConfig/hooks/useCloseRegistration.ts';
import useArchiveTour from '@/features/dashboard/tourConfig/hooks/useArchiveTour.ts';
import CancelTourModal from '@/features/dashboard/tours/components/CancelTourModal.tsx';
import useTourLink from '@/features/dashboard/tours/hooks/useTourLink.ts';
import { toast } from 'sonner';
import { TourStatus } from '@/features/dashboard/tours/types/TourStatus.ts';
import { RegistrationStatus } from '@/features/dashboard/tours/types/RegistrationStatus.ts';
import { useNavigate } from '@tanstack/react-router';
import useGetTour from '@/features/dashboard/tours/hooks/useGetTour.ts';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';
import useGetActivationReadiness from '@/features/dashboard/tours/hooks/useGetActivationReadiness.ts';
import { activationBlockers } from '@/features/dashboard/tours/types/TourActivationReadinessDto.utils.ts';
import useGetCancellationReadiness from '@/features/dashboard/tours/hooks/useGetCancellationReadiness.ts';
import useGetArchiveReadiness from '@/features/dashboard/tours/hooks/useGetArchiveReadiness.ts';
import { cn } from '@/lib/utils.ts';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import DetailsHeader from '@/components/details/DetailsHeader.tsx';
import TagPickerRow from '@/features/dashboard/tags/components/TagPickerRow.tsx';
import { TaggableType } from '@/features/dashboard/tags/types/TaggableType.ts';
import RegistrationStatusIndicator from '@/features/dashboard/tours/components/RegistrationStatusIndicator.tsx';
import { tourTabLinks } from '@/features/dashboard/tours/routes/tourTabs.ts';
import useGetTourStatistics from '@/features/dashboard/tours/hooks/useGetTourStatistics.ts';
import { formatDayMonthYearRange } from '@/utils/dateFormatters.ts';
import { pluralForm } from '@/utils/pluralize.ts';
import useGetTourFinanceDashboard from '@/features/dashboard/tours/hooks/useGetTourFinanceDashboard.ts';
import { keyedSegmentValue } from '@/components/charts/breakdownAdapter.ts';
import { BalanceSegmentKey } from '@/features/dashboard/tours/types/finance/balanceSegments.utils.ts';
import { formatPrice } from '@/utils/formatters.ts';

const TourPageHeader = () => {
  const { tourId } = tourRoute.useParams();
  const { data } = useGetTour(tourId);
  const tour = tourRoute.useLoaderData();
  const tourDto = data || tour;
  const navigate = useNavigate();

  const { confirmWithCallback, confirmDeleteWithCallback } =
    confirmDialogUseHook();

  const { tourLink, isLoading: isLoadingLink } = useTourLink(tourId);
  const { data: statistics } = useGetTourStatistics(tourId);
  const { data: finance } = useGetTourFinanceDashboard(tourId);

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const deleteTourMutation = useDeleteTour();
  const activateTourMutation = useActivateTour();
  const openRegistrationMutation = useOpenRegistration();
  const closeRegistrationMutation = useCloseRegistration();
  const archiveTourMutation = useArchiveTour();

  const { data: activationReadiness } = useGetActivationReadiness(
    tourId,
    tourDto.status,
  );
  const { data: cancellationReadiness } = useGetCancellationReadiness(
    tourId,
    tourDto.status,
  );
  const { data: archiveReadiness } = useGetArchiveReadiness(
    tourId,
    tourDto.status,
  );

  const activatable = activationReadiness?.activatable ?? false;
  const cancellable = cancellationReadiness?.cancellable ?? false;
  const archivable = archiveReadiness?.archivable ?? false;

  const getActivationTooltip = (): string | undefined => {
    if (!activationReadiness || activatable) return undefined;
    return activationBlockers(activationReadiness).join('. ');
  };

  const getCancellationTooltip = (): string | undefined => {
    if (!cancellationReadiness || cancellable) return undefined;
    if (!cancellationReadiness.isBeforeEndDate)
      return 'Nie można anulować wyjazdu po dacie zakończenia';
    return undefined;
  };

  const getArchiveTooltip = (): string | undefined => {
    if (!archiveReadiness || archivable) return undefined;
    if (!archiveReadiness.isAfterEndDate)
      return 'Nie można zarchiwizować wyjazdu przed datą zakończenia';
    return undefined;
  };

  // Backend liczy dopuszczalność akcji sam (status wyjazdu, okno zapisów, flaga
  // „zapisy w trakcie trwania") — front tylko tłumaczy `false` na powód.
  const canOpenRegistration =
    tourDto.registrationDetails.canOpenRegistration ?? false;

  const getOpenRegistrationTooltip = (): string | undefined => {
    if (canOpenRegistration) return undefined;
    if (tourDto.status === TourStatus.DRAFT)
      return 'Najpierw aktywuj wyjazd — zapisy można otworzyć dopiero na gotowym wyjeździe';
    if (tourDto.status !== TourStatus.READY)
      return 'Zapisy można otworzyć tylko na aktywnym wyjeździe';
    return 'Minął termin, do którego można prowadzić zapisy na ten wyjazd';
  };

  const meta: string[] = [
    formatDayMonthYearRange(tourDto.startDate, tourDto.endDate),
    timeUtils.rangeInDaysBetween(tourDto.startDate, tourDto.endDate, false),
  ];
  if (statistics) {
    meta.push(
      statistics.totalPlaces === null
        ? `${statistics.participantCount} ${pluralForm(statistics.participantCount, 'uczestnik', 'uczestników', 'uczestników')} · bez limitu`
        : `${statistics.participantCount} z ${statistics.totalPlaces} miejsc`,
    );
  }
  // Waluty się nie sumują, więc wpłaty idą obok siebie, a nie w jedną kwotę.
  if (finance && finance.balances.length > 0) {
    meta.push(
      `Wpłaty ${finance.balances
        .map((balance) =>
          formatPrice(
            keyedSegmentValue(balance, BalanceSegmentKey.PAID),
            balance.currency,
          ),
        )
        .join(' · ')}`,
    );
  }

  return (
    <>
      <DetailsHeader
        initials={tourDto.tourName.slice(0, 2).toUpperCase()}
        avatarTone="accent"
        title={tourDto.tourName}
        badges={[
          <TourStatusIndicator status={tourDto.status} />,
          <RegistrationStatusIndicator
            status={tourDto.registrationDetails.registrationStatus}
          />,
        ]}
        meta={meta}
        tags={<TagPickerRow targetType={TaggableType.TOUR} targetId={tourId} />}
        tabs={tourTabLinks(tourId)}
        // Kafle menu mobilnego mieszkają pod gołym adresem wyjazdu — nagłówek
        // dostaje go, żeby z zakładki było jak wrócić.
        menuLink={{ to: '/app/tours/$tourId', params: { tourId } }}
        actions={
          <div className="flex items-center gap-2">
            {tourDto.status !== TourStatus.DRAFT && (
              <ActionButton
                tone="outline"
                size="sm"
                disabled={isLoadingLink || !tourLink}
                onClick={() =>
                  window.open(tourLink, '_blank', 'noopener,noreferrer')
                }
              >
                <ExternalLink />
                Podgląd oferty
              </ActionButton>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ActionButton tone="accent" size="sm" iconOnly={true}>
                  <MoreVertical />
                </ActionButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[200px]">
                {/* Edytuj — always visible */}
                <DropdownMenuItem
                  className="gap-1"
                  onClick={() =>
                    navigate({
                      to: '/app/tours/$tourId/config/basics',
                      params: { tourId: tourDto.id },
                    })
                  }
                >
                  Edytuj
                </DropdownMenuItem>

                {/* Skopiuj link rezerwacji — not for DRAFT */}
                {tourDto.status !== TourStatus.DRAFT && (
                  <DropdownMenuItem
                    className="gap-1"
                    disabled={isLoadingLink}
                    onClick={() => {
                      if (isLoadingLink || !tourLink) {
                        toast.error('Link jeszcze się ładuje');
                        return;
                      }
                      navigator.clipboard.writeText(tourLink);
                      toast.success('Link skopiowany do schowka');
                    }}
                  >
                    {isLoadingLink
                      ? 'Ładowanie linku...'
                      : 'Skopiuj link rezerwacji'}
                  </DropdownMenuItem>
                )}

                {/* Aktywuj — DRAFT only */}
                {tourDto.status === TourStatus.DRAFT && (
                  <DropdownMenuItem
                    className={cn('gap-1', !activatable && 'opacity-50')}
                    title={getActivationTooltip()}
                    onSelect={(e) => {
                      if (!activatable) {
                        e.preventDefault();
                        return;
                      }
                      confirmWithCallback({
                        title: 'Aktywuj wyjazd',
                        description: (
                          <>
                            Czy na pewno chcesz aktywować wyjazd{' '}
                            <b>{tourDto.tourName}</b>?
                          </>
                        ),
                        onConfirm: () =>
                          activateTourMutation.mutate(tourDto.id),
                      });
                    }}
                  >
                    Aktywuj
                  </DropdownMenuItem>
                )}

                {/* Otwórz rejestrację — wszędzie poza otwartym oknem i odwołanym wyjazdem */}
                {![
                  RegistrationStatus.OPEN,
                  RegistrationStatus.CANCELLED,
                ].includes(tourDto.registrationDetails.registrationStatus) && (
                  <DropdownMenuItem
                    className={cn(
                      'gap-1',
                      !canOpenRegistration && 'opacity-50',
                    )}
                    title={getOpenRegistrationTooltip()}
                    onSelect={(e) => {
                      if (!canOpenRegistration) {
                        e.preventDefault();
                        return;
                      }
                      confirmWithCallback({
                        title: 'Otwórz rejestrację',
                        description: tourDto.registrationStartDate ? (
                          <>
                            Otwarcie rejestracji na obóz{' '}
                            <b>{tourDto.tourName}</b> jest zaplanowane na{' '}
                            <b>
                              {timeUtils.toDateTimeString(
                                tourDto.registrationStartDate,
                              )}
                            </b>
                            . Czy napewno chcesz otworzyć rejestrację już teraz?
                          </>
                        ) : (
                          <>
                            Czy napewno chcesz otworzyć rejestrację na obóz{' '}
                            <b>{tourDto.tourName}</b>?
                          </>
                        ),
                        onConfirm: () =>
                          openRegistrationMutation.mutate(tourDto.id),
                      });
                    }}
                  >
                    Otwórz rejestrację (publiczna)
                  </DropdownMenuItem>
                )}

                {/* Zamknij rejestrację — OPEN registration only */}
                {tourDto.registrationDetails.registrationStatus ===
                  RegistrationStatus.OPEN && (
                  <DropdownMenuItem
                    className="gap-1"
                    onClick={() => closeRegistrationMutation.mutate(tourDto.id)}
                  >
                    Zamknij rejestrację
                  </DropdownMenuItem>
                )}

                {/* Anuluj wyjazd — READY only */}
                {tourDto.status === TourStatus.READY && (
                  <DropdownMenuItem
                    className={cn(
                      'gap-1 text-error-200',
                      !cancellable && 'opacity-50',
                    )}
                    title={getCancellationTooltip()}
                    onSelect={(e) => {
                      if (!cancellable) {
                        e.preventDefault();
                        return;
                      }
                      setIsCancelModalOpen(true);
                    }}
                  >
                    Anuluj wyjazd
                  </DropdownMenuItem>
                )}

                {/* Usuń — DRAFT only */}
                {tourDto.status === TourStatus.DRAFT && (
                  <DropdownMenuItem
                    className="gap-1 text-error-200"
                    onClick={() => deleteTourMutation.mutate(tourDto.id)}
                  >
                    Usuń
                  </DropdownMenuItem>
                )}

                {/* Archiwizuj — READY or CANCELLED */}
                {(tourDto.status === TourStatus.READY ||
                  tourDto.status === TourStatus.CANCELLED) && (
                  <DropdownMenuItem
                    className={cn('gap-1', !archivable && 'opacity-50')}
                    title={getArchiveTooltip()}
                    onSelect={(e) => {
                      if (!archivable) {
                        e.preventDefault();
                        return;
                      }
                      confirmWithCallback({
                        title: 'Archiwizuj wyjazd',
                        description: (
                          <>
                            Czy na pewno chcesz zarchiwizować wyjazd{' '}
                            <b>{tourDto.tourName}</b>?
                          </>
                        ),
                        onConfirm: () => archiveTourMutation.mutate(tourDto.id),
                      });
                    }}
                  >
                    Archiwizuj
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />
      {isCancelModalOpen && (
        <CancelTourModal
          tourId={tourDto.id}
          tourName={tourDto.tourName}
          onClose={() => setIsCancelModalOpen(false)}
        />
      )}
    </>
  );
};

export default TourPageHeader;
